import type { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Image } from 'react-native';

import { BadgeArtwork } from '../../../assets/achievements';
import { IconKeyBadgeArtwork } from '../../../assets/achievements/badges';
import type {
	SvaraAchievementCategory,
	SvaraAchievementFamilyDto,
	SvaraAchievementTierDto,
} from '../../../services/api';
import { SVARA_ACHIEVEMENT_CATEGORIES } from '../../../services/api';
import { getFileStorageUrl } from '../../../utils/axiosInstance';
import { fillTranslationPlaceholders } from '../../../utils/helpers/globalBroadcastMessage';

export type AchievementTierView = SvaraAchievementTierDto & { unlocked: boolean };

export type AchievementView = {
	familyKey: string;
	category: SvaraAchievementCategory;
	categoryNameKey: string;
	nameKey: string;
	taglineKey: string;
	progress: number;
	tiers: AchievementTierView[];
	currentTier: AchievementTierView | null;
	nextTier: AchievementTierView | null;
	shownTier: AchievementTierView;
	locked: boolean;
	allTiersDone: boolean;
	featured: boolean;
	renderBadge: (size: number) => React.ReactNode;
};

export type AchievementCategoryOption = { category: SvaraAchievementCategory; nameKey: string };

const BADGE_COUNT = BadgeArtwork.unlocked.length;

export const formatAchievementValue = (value: number) =>
	String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const badgeIndex = (displayTier: number) => Math.min(Math.max(displayTier, 1), BADGE_COUNT) - 1;

type BadgeTier = Pick<AchievementTierView, 'displayTier' | 'iconFileId' | 'unlocked'> & {
	iconKey?: string;
};

const iconKeyBadge = (iconKey: string | undefined) =>
	iconKey ? IconKeyBadgeArtwork[iconKey] : undefined;

// The tier's own Figma art when there is one for its iconKey, else the generic per-tier art.
export const badgeSource = (iconKey: string | undefined, displayTier: number, locked: boolean) => {
	const art = iconKeyBadge(iconKey);
	if (art) return locked ? art.locked : art.unlocked;
	return locked
		? BadgeArtwork.locked[badgeIndex(displayTier)]
		: BadgeArtwork.unlocked[badgeIndex(displayTier)];
};

const bundledBadge = (tier: BadgeTier, locked: boolean) => (size: number) => (
	<Image
		source={badgeSource(tier.iconKey, tier.displayTier, locked)}
		style={{ width: size, height: size }}
		resizeMode='cover'
	/>
);

type UploadedBadgeProps = {
	fileId: string;
	size: number;
	renderFallback: (size: number) => React.ReactNode;
};

const UploadedBadge = ({ fileId, size, renderFallback }: UploadedBadgeProps) => {
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		setFailed(false);
	}, [fileId]);

	if (failed) return <>{renderFallback(size)}</>;

	return (
		<Image
			source={{ uri: getFileStorageUrl(fileId) }}
			style={{ width: size, height: size }}
			resizeMode='contain'
			onError={() => setFailed(true)}
		/>
	);
};

// The Figma art wins over an uploaded icon; uploads only cover tiers it has no art for.
export const tierBadge = (tier: BadgeTier) => {
	const fallback = bundledBadge(tier, !tier.unlocked);
	if (!tier.unlocked || !tier.iconFileId || iconKeyBadge(tier.iconKey)) return fallback;

	const fileId = tier.iconFileId;
	return (size: number) => <UploadedBadge fileId={fileId} size={size} renderFallback={fallback} />;
};

const toTierView = (tier: SvaraAchievementTierDto): AchievementTierView => ({
	...tier,
	unlocked: tier.unlockedAt !== null,
});

export const toAchievementView = (
	dto: SvaraAchievementFamilyDto,
	featuredFamilyKeys: readonly string[],
): AchievementView | null => {
	const tiers = (dto.tiers ?? []).map(toTierView);
	const [firstTier] = tiers;
	if (!firstTier) return null;

	const currentTier = dto.currentTier ? toTierView(dto.currentTier) : null;
	const shownTier = currentTier ?? firstTier;

	return {
		familyKey: dto.familyKey,
		category: dto.category,
		categoryNameKey: dto.categoryNameKey,
		nameKey: dto.nameKey,
		taglineKey: dto.taglineKey,
		progress: dto.progress,
		tiers,
		currentTier,
		nextTier: dto.nextTier ? toTierView(dto.nextTier) : null,
		shownTier,
		locked: currentTier === null,
		allTiersDone: tiers.every(tier => tier.unlocked),
		featured: featuredFamilyKeys.includes(dto.familyKey),
		renderBadge: tierBadge(currentTier ? shownTier : { ...shownTier, unlocked: false }),
	};
};

export const toAchievementViews = (
	families: readonly SvaraAchievementFamilyDto[],
	featuredFamilyKeys: readonly string[],
): AchievementView[] =>
	families
		.map(family => toAchievementView(family, featuredFamilyKeys))
		.filter((view): view is AchievementView => view !== null);

export const presentCategories = (views: readonly AchievementView[]): AchievementCategoryOption[] =>
	SVARA_ACHIEVEMENT_CATEGORIES.flatMap(category => {
		const view = views.find(candidate => candidate.category === category);
		return view ? [{ category, nameKey: view.categoryNameKey }] : [];
	});

const ACHIEVEMENT_TEXT: Record<string, string> = {
	T_SITE_ACHIEVEMENTS_TITLE: 'Achievements',
	T_SITE_ACHIEVEMENTS_DETAILS_TITLE: 'Achievement Details',
	T_SITE_ACHIEVEMENTS_SUBTITLE: 'Complete challenges, earn achievements and show your skills!',
	T_SITE_ACHIEVEMENTS_FILTER_ALL: 'All',
	T_SITE_ACHIEVEMENTS_FILTER_UNLOCKED: 'Unlocked',
	T_SITE_ACHIEVEMENTS_FILTER_LOCKED: 'Locked',
	T_SITE_ACHIEVEMENTS_FILTER_CATEGORIES: 'Categories',
	T_SITE_ACHIEVEMENTS_SELECTED_FOR_PROFILE: 'Selected for Profile',
	T_SITE_ACHIEVEMENTS_SHELF_EMPTY: 'YOUR SELECTED ACHIEVEMENTS WILL APPEAR HERE',
	T_SITE_ACHIEVEMENTS_SHELF_HINT: 'Choose up to 5 achievements to display on your profile.',
	T_SITE_ACHIEVEMENTS_SHELF_FULL: 'Maximum reached — remove one to pick another.',
	T_SITE_ACHIEVEMENTS_ADD_TO_PROFILE: 'ADD TO PROFILE',
	T_SITE_ACHIEVEMENTS_REMOVE_FROM_PROFILE: 'REMOVE FROM PROFILE',
	T_SITE_ACHIEVEMENTS_OK: 'OK',
	T_SITE_ACHIEVEMENTS_MAX_TITLE: 'You can display up to 5 achievements',
	T_SITE_ACHIEVEMENTS_MAX_BODY: 'Remove one from your profile to add this achievement.',
	T_SITE_ACHIEVEMENTS_EMPTY_TITLE: 'Nothing here yet',
	T_SITE_ACHIEVEMENTS_EMPTY_BODY: 'No achievements match this filter. Try another category.',
	T_SITE_ACHIEVEMENTS_ERROR_LOAD: 'Achievements could not be loaded. Please try again.',
	T_SITE_ACHIEVEMENTS_ERROR_SAVE: 'Your profile selection could not be saved. Please try again.',
	T_SITE_ACHIEVEMENTS_ERROR_STALE:
		'Your profile selection was changed on another device. Please check it and try again.',
	T_SITE_ACHIEVEMENTS_ERROR_NOT_UNLOCKED:
		'Only unlocked achievements can be shown on your profile.',
	T_SITE_ACHIEVEMENTS_RETRY: 'RETRY',
	T_SITE_ACHIEVEMENTS_ALL_TIERS_DONE: 'All tiers completed',
	T_SITE_ACHIEVEMENTS_BACK: 'Back',
	T_SITE_ACHIEVEMENTS_CLOSE: 'Close',
	T_SITE_ACHIEVEMENTS_CLEAR_CATEGORY: 'Clear category',
	T_SITE_ACHIEVEMENTS_ADD_A11Y: 'Add %% to profile',
	T_SITE_ACHIEVEMENTS_REMOVE_A11Y: 'Remove %% from profile',
	T_SITE_ACHIEVEMENTS_UNLOCKED: 'Achievement unlocked: %%',
	T_SITE_ACHIEVEMENTS_FEATURED: 'Featured Achievements',
	T_SITE_ACHIEVEMENTS_VIEW_ALL: 'View All',
};

export const achievementText = (t: TFunction, key: string, values: string[] = []) => {
	const text = t(key, { defaultValue: ACHIEVEMENT_TEXT[key] ?? key });
	return values.length ? fillTranslationPlaceholders(text, values) : text;
};

export const tierRequirement = (t: TFunction, tier: AchievementTierView) =>
	fillTranslationPlaceholders(t(tier.descriptionKey), [formatAchievementValue(tier.target)]);
